import { useId, type ComponentType, type ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface SettingsSectionProps {
  /** Pass the icon component itself, e.g. `icon={User}` — not an element. */
  icon: ComponentType<{ className?: string }>
  title: string
  description?: string
  /** Rendered at the far end of the header, e.g. a refresh control. */
  headerAction?: ReactNode
  children: ReactNode
  className?: string
}

/** One settings card: a titled panel on `bg-surface-raised`. */
export function SettingsSection({
  icon: Icon,
  title,
  description,
  headerAction,
  children,
  className,
}: SettingsSectionProps) {
  const headingId = useId()

  return (
    <section
      aria-labelledby={headingId}
      className={cn('rounded-card border border-border bg-surface-raised', className)}
    >
      <header className="flex items-start gap-3 border-b border-border px-4 py-4 sm:px-5">
        <span
          aria-hidden="true"
          className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-hover"
        >
          <Icon className="h-[1.125rem] w-[1.125rem] text-text-muted" />
        </span>

        <div className="min-w-0 flex-1">
          <h2 id={headingId} className="text-base font-semibold text-text">
            {title}
          </h2>
          {description ? <p className="mt-0.5 text-sm text-text-muted">{description}</p> : null}
        </div>

        {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
      </header>

      <div className="px-4 py-4 sm:px-5">{children}</div>
    </section>
  )
}
